import * as THREE from 'three';
import { orderBoundaryVertices } from './boundaryDetection';

/**
 * Map boundary vertices to circular segments based on angular position
 * @param {Array} boundaryIndices - Boundary vertex indices
 * @param {Array} boundaryPositions - Boundary positions (world space)
 * @param {THREE.Vector3} centroid - Boundary centroid
 * @param {Array} flareValues - Segment division percentages [20, 40, 60, 80, 100]
 * @returns {Map} Map of vertexIndex -> segmentIndex
 */
export function mapVerticesToSegments(boundaryIndices, boundaryPositions, centroid, flareValues) {
  const vertexSegmentMap = new Map();

  boundaryPositions.forEach((pos, idx) => {
    const vertexIndex = boundaryIndices[idx];

    // Compute angle in XZ plane from centroid
    // 0° at top (north/-Z) when viewed from above, clockwise progression
    const dx = pos.x - centroid.x;
    const dz = pos.z - centroid.z;

    let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    // Convert to percentage [0, 100)
    const anglePercent = (angle / 360) * 100;

    const segmentIndex = findSegmentForDialPercent(anglePercent, flareValues);
    vertexSegmentMap.set(vertexIndex, segmentIndex);
  });

  return vertexSegmentMap;
}

/**
 * Which dial segment a percentage [0, 100) falls into (same rules for angle or arc-length).
 * Handles wrap-around at 0%/100% boundary.
 * @param {number} dialPercent - Percentage along dial [0, 100)
 * @param {Array<number>} flareValues - Segment boundaries
 * @returns {number} Segment index
 */
export function findSegmentForDialPercent(dialPercent, flareValues) {
  const n = flareValues.length;
  if (n === 0) return 0;

  for (let i = 0; i < n; i++) {
    const startPercent = i === 0 ? flareValues[n - 1] : flareValues[i - 1];
    const endPercent = flareValues[i];

    // Check if this segment wraps around the 0%/100% boundary
    if (endPercent < startPercent) {
      if (dialPercent >= startPercent || dialPercent < endPercent) {
        return i;
      }
    } else {
      if (dialPercent >= startPercent && dialPercent < endPercent) {
        return i;
      }
    }
  }

  return n - 1;
}

/**
 * Label boundary seeds by arc length along ordered trim loop (dial % = % of perimeter).
 * @param {Array<number>} boundaryIndices - Boundary vertex indices
 * @param {Array<THREE.Vector3>} boundaryPositions - World positions (same order as boundaryIndices)
 * @param {Array<number>} flareValues - Dial breakpoints
 * @returns {Map<number, number>|null} vertexIndex -> segmentIndex, or null if unusable
 */
export function mapBoundaryVerticesByArcLength(boundaryIndices, boundaryPositions, flareValues) {
  const map = new Map();
  if (!boundaryIndices.length || !flareValues.length) return null;

  const ordered = orderBoundaryVertices(boundaryIndices, boundaryPositions);
  const n = ordered.length;
  if (n < 3) return null;

  const posFor = (idx) => {
    const ai = boundaryIndices.indexOf(idx);
    return ai >= 0 ? boundaryPositions[ai] : null;
  };

  let perimeter = 0;
  const edgeLens = [];
  for (let i = 0; i < n; i++) {
    const a = posFor(ordered[i]);
    const b = posFor(ordered[(i + 1) % n]);
    if (!a || !b) return null;
    const len = a.distanceTo(b);
    edgeLens.push(len);
    perimeter += len;
  }

  if (perimeter <= 1e-12) return null;

  const cumulative = new Map();
  cumulative.set(ordered[0], 0);
  let s = 0;
  for (let k = 1; k < n; k++) {
    s += edgeLens[k - 1];
    cumulative.set(ordered[k], s);
  }

  for (let k = 0; k < n; k++) {
    const idx = ordered[k];
    const cum = cumulative.get(idx) ?? 0;
    const percent = (cum / perimeter) * 100;
    map.set(idx, findSegmentForDialPercent(percent, flareValues));
  }

  return map;
}

/**
 * Extend segment mapping to nearby vertices (within falloff range)
 * Assigns vertices based on angular position (which segment sector they're in)
 * NOT by nearest boundary vertex (which causes incorrect cross-segment assignment)
 *
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {Map} boundarySegmentMap - Boundary vertex -> segment mapping
 * @param {Float32Array} distances - Distance from boundary for all vertices
 * @param {number} maxFalloffDistance - Maximum falloff distance
 * @param {THREE.Vector3} centroid - Boundary centroid for angle calculation
 * @param {Array} flareValues - Segment division percentages
 * @returns {Map} Extended vertex -> segment mapping
 */
export function extendSegmentMapping(geometry, boundarySegmentMap, distances, maxFalloffDistance, centroid = null, flareValues = null) {
  const extendedMap = new Map(boundarySegmentMap);
  const positions = geometry.attributes.position;

  // If centroid and flareValues not provided, fall back to nearest boundary vertex method
  // This maintains backward compatibility but should not be used for flares
  const useAngularAssignment = centroid && flareValues;

  // For each vertex within falloff range, assign based on angular position
  for (let i = 0; i < positions.count; i++) {
    if (distances[i] > maxFalloffDistance) continue;
    if (extendedMap.has(i)) continue; // Already mapped (is a boundary vertex)

    const vi = new THREE.Vector3().fromBufferAttribute(positions, i);

    if (useAngularAssignment) {
      // CORRECT METHOD: Assign based on angular position in XZ plane
      // This ensures vertices stay in their correct angular segment
      const dx = vi.x - centroid.x;
      const dz = vi.z - centroid.z;

      // Use same angle calculation as mapVerticesToSegments (0° at top, clockwise)
      let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
      if (angle < 0) angle += 360;

      const anglePercent = (angle / 360) * 100;
      const segmentIndex = findSegmentForDialPercent(anglePercent, flareValues);

      extendedMap.set(i, segmentIndex);
    } else {
      // OLD METHOD: Find nearest boundary vertex (causes cross-segment bugs)
      let nearestBoundaryVertex = null;
      let minDist = Infinity;

      for (const [boundaryIdx, segmentIdx] of boundarySegmentMap) {
        const vb = new THREE.Vector3().fromBufferAttribute(positions, boundaryIdx);
        const dist = vi.distanceTo(vb);

        if (dist < minDist) {
          minDist = dist;
          nearestBoundaryVertex = boundaryIdx;
        }
      }

      if (nearestBoundaryVertex !== null) {
        extendedMap.set(i, boundarySegmentMap.get(nearestBoundaryVertex));
      }
    }
  }

  return extendedMap;
}

/**
 * Create a map of vertex to nearest boundary vertex info
 * This allows each vertex to use a local boundary reference instead of a global max Y,
 * and inherit deflection direction from its nearest boundary vertex
 * NOW: Only searches within the SAME SEGMENT to prevent cross-segment contamination
 *
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {Map} boundarySegmentMap - Boundary vertex -> segment mapping
 * @param {Map} boundaryYMap - Boundary vertex index -> world Y position
 * @param {Float32Array} distances - Distance from boundary for all vertices
 * @param {number} maxDistance - Maximum distance to consider
 * @param {Map} extendedSegmentMap - Extended vertex -> segment mapping (for filtering)
 * @returns {Map} vertex index -> {boundaryY, boundaryIndex}
 */
export function createVertexBoundaryYMap(geometry, boundarySegmentMap, boundaryYMap, distances, maxDistance, extendedSegmentMap = null) {
  const vertexBoundaryMap = new Map();
  const positions = geometry.attributes.position;

  // First, add boundary vertices themselves (use their own Y and index)
  for (const [boundaryIdx, _] of boundarySegmentMap) {
    const boundaryY = boundaryYMap.get(boundaryIdx);
    if (boundaryY !== undefined) {
      vertexBoundaryMap.set(boundaryIdx, {
        boundaryY: boundaryY,
        boundaryIndex: boundaryIdx
      });
    }
  }

  // For non-boundary vertices, find nearest boundary vertex IN THE SAME SEGMENT
  for (let i = 0; i < positions.count; i++) {
    if (distances[i] > maxDistance) continue;
    if (vertexBoundaryMap.has(i)) continue; // Already a boundary vertex

    // Get this vertex's segment (if extended map provided)
    const vertexSegment = extendedSegmentMap ? extendedSegmentMap.get(i) : null;

    let nearestBoundaryIdx = null;
    let nearestBoundaryY = null;
    let minDist = Infinity;

    const vi = new THREE.Vector3().fromBufferAttribute(positions, i);

    for (const [boundaryIdx, boundarySegment] of boundarySegmentMap) {
      // CRITICAL FIX: Only consider boundary vertices in the SAME segment
      if (vertexSegment !== null && boundarySegment !== vertexSegment) {
        continue; // Skip boundary vertices from other segments
      }

      const vb = new THREE.Vector3().fromBufferAttribute(positions, boundaryIdx);
      const dist = vi.distanceTo(vb);

      if (dist < minDist) {
        minDist = dist;
        nearestBoundaryIdx = boundaryIdx;
        nearestBoundaryY = boundaryYMap.get(boundaryIdx);
      }
    }

    if (nearestBoundaryIdx !== null && nearestBoundaryY !== null) {
      vertexBoundaryMap.set(i, {
        boundaryY: nearestBoundaryY,
        boundaryIndex: nearestBoundaryIdx
      });
    }
  }

  return vertexBoundaryMap;
}
